import type { ComponentType, ReactNode } from "react";
import {
    AlertOctagon,
    ArrowLeftCircle,
    ArrowRight,
    Ban,
    Check,
    CheckCircle2,
    Loader2,
    MoreHorizontal,
    Pencil,
    Play,
    Printer,
    RefreshCcw,
    RotateCcw,
    Save,
    Send,
    Trash2,
    Undo2,
    X,
} from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { appIcon } from "./icons";
import { enumKey } from "./cell_utils";
import { evalRowInvisible } from "../widgets/rowVisibility";
import { useWorkflowTransitions } from "../react/queries/useWorkflowTransitions";
import { commandSignature } from "../core";
import { confirm } from "./ConfirmDialog";
import type {
    FieldDef,
    FormButtonElement,
    FormElement,
    FormFieldElement,
    FormStatusbarElement,
    RecordData,
    WorkflowTransition,
} from "../core";
import { usePendingCommand } from "../react/hooks/usePendingCommand";

// FormHeader — the top strip of the form sheet declared via DSL `<header>`.
//   - Action `<button>` elements + non-statusbar fields render LEFT.
//   - `<statusbar/>` elements + `<field widget="statusbar"/>` render RIGHT
//     as a pipeline of stage pills.
//   - Dynamic workflow-transition pills render alongside the static buttons,
//     fetched once per (modelKey, recordRef).
//
// Every button goes through the platform RUN_BUTTON command via the callbacks
// from ActionManager. Buttons subscribe per-signature to the central pending
// registry so each one disables + shows its own spinner independently. NO
// progress toasts.

interface FormHeaderProps {
    elements: FormElement[];
    record: RecordData | null;
    fieldDefs: FieldDef[];
    actionSlug: string;
    modelKey: string;
    recordRef: string | null;
    onRunButton?: (button: FormButtonElement) => void;
    // Receives both the transition CODE (wire identifier the workflow engine
    // resolves against) and the human NAME so the dispatcher can surface a
    // user-friendly success toast ("Convert to Lead done") instead of the
    // bare wire code.
    onRunTransition?: (code: string, name: string) => Promise<void> | void;
    /** Revamp: a cockpit <status> card owns the statusbar + transitions +
        action verbs, so suppress them here to avoid a duplicate top row. */
    suppressWorkflow?: boolean;
}

const isStatusbarEl = (el: FormElement): boolean =>
    el.type === "statusbar" || (el.type === "field" && el.widget === "statusbar");

// `<related>` blocks declared in `<header>` are physically rendered in the
// form TOOLBAR (FormControlPanel), not the form's header strip. Skip them
// here to avoid a double-render. ActionManager extracts these blocks before
// passing the header element list to FormHeader, so this is belt-and-braces.
const isRelatedEl = (el: FormElement): boolean => el.type === "related";

export function FormHeader({
    elements,
    record,
    fieldDefs,
    actionSlug,
    modelKey,
    recordRef,
    onRunButton,
    onRunTransition,
    suppressWorkflow = false,
}: FormHeaderProps): ReactNode {
    const renderable = elements.filter((el) => !isRelatedEl(el));
    // Form-view revamp (Enh 14, Stage 4): when a cockpit <status> card is
    // present it owns the statusbar + transition buttons + action verbs, so the
    // header drops them here to avoid a duplicate top row.
    const left = renderable
        .filter((el) => !isStatusbarEl(el))
        .filter((el) => !(suppressWorkflow && el.type === "button"));
    const right = suppressWorkflow ? [] : renderable.filter(isStatusbarEl);
    const fieldMap = new Map(fieldDefs.map((f) => [f.name, f]));

    const hasWorkflow = right.some((el) => el.type === "statusbar");
    const transitionsQ = useWorkflowTransitions(modelKey, recordRef, hasWorkflow);
    const transitions = transitionsQ.data ?? [];

    if (left.length === 0 && right.length === 0) return null;

    return (
        <div className="form-header">
            <div className="form-header__left">
                {left.map((el, i) => (
                    <FormHeaderElement
                        key={`l-${i}`}
                        el={el}
                        record={record}
                        fieldMap={fieldMap}
                        actionSlug={actionSlug}
                        recordRef={recordRef}
                        onRunButton={onRunButton}
                    />
                ))}
                {hasWorkflow ? (
                    <TransitionButtons
                        transitions={transitions}
                        actionSlug={actionSlug}
                        recordRef={recordRef}
                        onRun={onRunTransition}
                    />
                ) : null}
            </div>
            <div className="form-header__right">
                {right.map((el, i) => (
                    <FormHeaderElement
                        key={`r-${i}`}
                        el={el}
                        record={record}
                        fieldMap={fieldMap}
                        actionSlug={actionSlug}
                        recordRef={recordRef}
                        onRunButton={onRunButton}
                    />
                ))}
            </div>
        </div>
    );
}

// Renders one button per available workflow transition. Each pill subscribes
// to the central pending registry under the RUN_BUTTON signature it will
// dispatch, so its spinner state stays in sync with the bus.
function TransitionButtons({
    transitions,
    actionSlug,
    recordRef,
    onRun,
}: {
    transitions: WorkflowTransition[];
    actionSlug: string;
    recordRef: string | null;
    onRun?: (code: string, name: string) => Promise<void> | void;
}): ReactNode {
    if (transitions.length === 0 || !recordRef) return null;
    const click = async (t: WorkflowTransition) => {
        if (t.requires_approval) {
            const ok = await confirm({
                title: t.name,
                message: `This will request approval for "${t.name}". Continue?`,
                confirmLabel: "Request approval",
                cancelLabel: "Cancel",
            });
            if (!ok) return;
        }
        void onRun?.(t.code, t.name);
    };
    return (
        <>
            {transitions.map((t) => (
                <TransitionButton
                    key={t.code}
                    transition={t}
                    actionSlug={actionSlug}
                    recordRef={recordRef}
                    onClick={click}
                />
            ))}
        </>
    );
}

function TransitionButton({
    transition,
    actionSlug,
    recordRef,
    onClick,
}: {
    transition: WorkflowTransition;
    actionSlug: string;
    recordRef: string;
    onClick: (t: WorkflowTransition) => void;
}): ReactNode {
    // Signature MUST match the one ActionManager.onRunFormTransition dispatches
    // with. `commandSignature` derives from the command type+payload so as long
    // as the (commandType:"workflow", actionSlug, commandName:code, recordUuid)
    // tuple matches, the spinner state syncs.
    const signature = commandSignature({
        type: "RUN_BUTTON",
        payload: {
            actionSlug,
            recordUuid: recordRef,
            commandName: transition.code,
            commandType: "workflow",
        },
    });
    const pending = usePendingCommand(signature);
    return (
        <button
            type="button"
            className={`form-header__btn form-header__btn--transition${transition.requires_approval ? " is-approval" : ""}`}
            onClick={() => onClick(transition)}
            disabled={pending}
            title={transition.requires_approval ? `${transition.name} (requires approval)` : transition.name}
            data-viewid="ede-statusbar-action"
            data-transition={transition.code}
        >
            {pending ? <Loader2 size={13} className="form-header__btn-spinner" /> : null}
            <span>{transition.name}</span>
        </button>
    );
}

function FormHeaderElement({
    el,
    record,
    fieldMap,
    actionSlug,
    recordRef,
    onRunButton,
}: {
    el: FormElement;
    record: RecordData | null;
    fieldMap: Map<string, FieldDef>;
    actionSlug: string;
    recordRef: string | null;
    onRunButton?: (b: FormButtonElement) => void;
}): ReactNode {
    if (el.type === "button") {
        return (
            <HeaderButton
                el={el}
                record={record}
                actionSlug={actionSlug}
                recordRef={recordRef}
                onClick={onRunButton}
            />
        );
    }
    if (el.type === "statusbar") {
        return <WorkflowStatusBar el={el} record={record} />;
    }
    if (el.type === "field") {
        if (el.widget === "statusbar") {
            return <StatusbarWidget el={el} record={record} fieldDef={fieldMap.get(el.name)} />;
        }
        if (!record) return null;
        const value = record[el.name];
        if (value == null || value === "") return null;
        return (
            <span className="form-header__field">
                <span className="form-header__field-label">{el.string ?? el.name}</span>
                <span className="form-header__field-value">{String(value)}</span>
            </span>
        );
    }
    return null;
}

// Keyword → icon map. When a DSL `<button>` doesn't carry an explicit
// `icon=` (e.g. workflow transition buttons synthesised from
// `ir.workflow.transition` rows), we infer one from the verb so the
// header row reads as iconified actions instead of bare text. Matches
// against the lowercased label; first hit wins, so order keywords from
// most-specific to least.
const ICON_KEYWORDS: ReadonlyArray<[RegExp, ComponentType<{ size?: number }>]> = [
    [/\bsubmit\b/, Send],
    [/\bsend\b/, Send],
    [/\bapprove\b/, CheckCircle2],
    [/\baccept\b/, Check],
    [/\bconfirm\b/, Check],
    [/\breject\b/, X],
    [/\b(cancel|abort)\b/, Ban],
    [/\breturn\b/, Undo2],
    [/\brecall\b/, ArrowLeftCircle],
    [/\bdiscard\b/, Undo2],
    [/\breset\b/, RotateCcw],
    [/\bre(open|activate|fresh|try)\b/, RefreshCcw],
    [/\bdelete\b/, Trash2],
    [/\barchive\b/, Trash2],
    [/\bedit\b/, Pencil],
    [/\bsave\b/, Save],
    [/\bprint\b/, Printer],
    [/\b(escalate|alert|warn)\b/, AlertOctagon],
    [/\b(start|run|execute|trigger)\b/, Play],
];

function defaultIconForLabel(label: string): ComponentType<{ size?: number }> {
    const lc = label.toLowerCase();
    for (const [re, Icon] of ICON_KEYWORDS) {
        if (re.test(lc)) return Icon;
    }
    return ArrowRight;  // fallback: a generic "go" arrow so every verb has an icon
}

export function HeaderButton({
    el,
    record,
    actionSlug,
    recordRef,
    onClick,
    className = "form-header__btn",
}: {
    el: FormButtonElement;
    record: RecordData | null;
    actionSlug: string;
    recordRef: string | null;
    onClick?: (b: FormButtonElement) => void;
    className?: string;
}): ReactNode {
    if (record && evalRowInvisible(el.invisible, record)) return null;
    const dynamicLabel = el.labelField && record ? String(record[el.labelField] ?? "") : "";
    const label = dynamicLabel || el.label || el.name;
    // Explicit DSL `icon=` wins; otherwise infer from the verb so
    // workflow-transition buttons (which carry no icon metadata) still
    // get a sensible glyph.
    const Icon = el.icon ? appIcon(el.icon) : defaultIconForLabel(label);

    // Per-button pending — derive the SAME signature ActionManager dispatches.
    // When recordRef is null (new-record draft) the button is non-dispatchable;
    // we still render it disabled so layout doesn't shift.
    const signature = recordRef
        ? commandSignature({
              type: "RUN_BUTTON",
              payload: {
                  actionSlug,
                  recordUuid: recordRef,
                  commandName: el.command ?? "",
                  commandType: el.commandType ?? null,
              },
          })
        : "";
    const pending = usePendingCommand(signature);

    const confirmAndClick = async () => {
        if (!recordRef) return;
        if (pending) return;
        // DSL `confirm="…"` attribute — gate the dispatch behind a themed
        // Radix Dialog (matches the rest of the app's chrome) instead of the
        // browser's native window.confirm. The action verb's label drives
        // the dialog title; `el.confirm` becomes the message body.
        if (el.confirm) {
            const ok = await confirm({
                title: el.label || el.name,
                message: el.confirm,
                confirmLabel: el.label || "Confirm",
                cancelLabel: "Cancel",
            });
            if (!ok) return;
        }
        onClick?.(el);
    };
    return (
        <button
            type="button"
            className={className}
            onClick={confirmAndClick}
            disabled={pending || !recordRef}
            title={label}
            data-viewid="ede-form-button"
            data-action={el.name}
        >
            {pending ? (
                <Loader2 size={13} className="form-header__btn-spinner" />
            ) : Icon ? (
                <Icon size={13} />
            ) : null}
            <span>{label}</span>
        </button>
    );
}

// `<field widget="statusbar"/>` — pipeline pills derived from the field's
// enum selection.
function StatusbarWidget({
    el,
    record,
    fieldDef,
}: {
    el: FormFieldElement;
    record: RecordData | null;
    fieldDef?: FieldDef;
}): ReactNode {
    if (!fieldDef || !record) return null;
    const visibleOpt = el.options?.statusbar_visible ?? "";
    const allKeys: string[] = (fieldDef.selection ?? []).map(([k]) => k);
    const orderedKeys: string[] = visibleOpt
        ? visibleOpt.split(",").map((s) => s.trim()).filter(Boolean)
        : allKeys;
    const selectionMap = new Map(fieldDef.selection ?? []);
    const current = String(enumKey(record[el.name]) ?? "");
    const currentIdx = orderedKeys.indexOf(current);
    // Hidden keys: anything in the field's full selection that wasn't
    // listed in statusbar_visible AND isn't the current value. The
    // current value renders inline regardless (the user must see what
    // state they're actually in). Without `statusbar_visible`, no
    // overflow — every key is inline already.
    const overflowKeys = visibleOpt
        ? allKeys.filter((k) => !orderedKeys.includes(k) && k !== current)
        : [];
    return (
        <div className="form-statusbar">
            {orderedKeys.map((key, i) => {
                const label = selectionMap.get(key) ?? key;
                const isCurrent = key === current;
                const isPast = currentIdx >= 0 && i < currentIdx;
                return (
                    <span
                        key={key}
                        className={`form-statusbar__stage ${isCurrent ? "is-current" : ""} ${isPast ? "is-past" : ""}`}
                        data-viewid="ede-statusbar-stage"
                        data-stage={key}
                    >
                        {label}
                    </span>
                );
            })}
            {overflowKeys.length > 0 ? (
                <StatusbarOverflow
                    items={overflowKeys.map((k) => ({ key: k, label: selectionMap.get(k) ?? k }))}
                />
            ) : null}
        </div>
    );
}

// `<statusbar field="..."/>` — workflow-driven pipeline.
//
// Layout split:
//   - Happy-path stages (everything that's NOT terminal-and-not-current)
//     render inline as the visible stepper.
//   - Terminal stages other than the current one (Lost / Cancelled /
//     Rejected / Expired / …) collapse into a trailing `…` dropdown so
//     long pipelines stay readable. Clicking the dots opens a
//     non-interactive list of the off-the-happy-path states. The
//     CURRENT stage always renders inline even when terminal — the
//     user must see what state they're actually in.
function WorkflowStatusBar({
    el,
    record,
}: {
    el: FormStatusbarElement;
    record: RecordData | null;
}): ReactNode {
    if (!record) return null;
    const stages = el.allStages ?? [];
    if (stages.length === 0) return null;
    const current = String(record[el.field] ?? "");
    const currentIdx = stages.findIndex((s) => s.code === current);
    const isTerminalStage = (s: typeof stages[number], isCurrent: boolean): boolean =>
        s.isFinal === true && !isCurrent;
    const visibleStages = stages.filter((s) => !isTerminalStage(s, s.code === current));
    const overflowStages = stages.filter((s) => isTerminalStage(s, s.code === current));
    return (
        <div className="form-statusbar form-statusbar--workflow">
            {visibleStages.map((stage) => {
                const stageIdx = stages.findIndex((s) => s.code === stage.code);
                const isCurrent = stage.code === current;
                const isPast = currentIdx >= 0 && stageIdx < currentIdx;
                const cls = [
                    "form-statusbar__stage",
                    isCurrent ? "is-current" : "",
                    isPast ? "is-past" : "",
                ].filter(Boolean).join(" ");
                return (
                    <span key={stage.code} className={cls} data-viewid="ede-statusbar-stage" data-stage={stage.code}>
                        {stage.label}
                    </span>
                );
            })}
            {overflowStages.length > 0 ? (
                <StatusbarOverflow
                    items={overflowStages.map((s) => ({ key: s.code, label: s.label }))}
                />
            ) : null}
        </div>
    );
}

// Trailing `…` dropdown for off-the-happy-path / hidden stages. Render-
// only — items are non-interactive labels matching the inline pill style
// (no click handler, no state mutation). Shared between the workflow
// statusbar and the enum-driven StatusbarWidget so they look identical.
function StatusbarOverflow({ items }: { items: { key: string; label: string }[] }) {
    if (items.length === 0) return null;
    return (
        <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
                <button
                    type="button"
                    className="form-statusbar__stage form-statusbar__stage--overflow"
                    aria-label={`Show ${items.length} more stage${items.length === 1 ? "" : "s"}`}
                    title="More stages"
                    data-testid="form-statusbar-overflow"
                >
                    <MoreHorizontal size={14} />
                </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
                <DropdownMenu.Content
                    className="form-statusbar__overflow-menu"
                    align="end"
                    sideOffset={4}
                >
                    {items.map((it) => (
                        <DropdownMenu.Item
                            key={it.key}
                            className="form-statusbar__overflow-item"
                            onSelect={(e) => e.preventDefault()}
                        >
                            {it.label}
                        </DropdownMenu.Item>
                    ))}
                </DropdownMenu.Content>
            </DropdownMenu.Portal>
        </DropdownMenu.Root>
    );
}
